import { css } from "@emotion/react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { SettingsAnnouncementBarType } from "@swo/storefront";
import { ContentContainer } from "../_shared-components/arrangement/Container";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import React from "react";
import { FONTS, fontSize } from "../../styles/fonts";
import { CloseButtonSvg } from "../../assets";
import { AnnouncementContent } from "./AnnouncementContent";
import { CSS_MQ } from "../../styles/breakpoints";

export const AnnouncementBar: ReactTFC<{
  announcements: Array<SettingsAnnouncementBarType>;
  activeIdx: number;
  onNotificationDismissed: () => void;
  positionStatically: boolean;
}> = props => {
  function getAnnouncements() {
    const current = props.announcements[props.activeIdx];

    if (props.announcements.length === 1) {
      return {
        current,
        previous: undefined,
        next: undefined,
      };
    }

    const previousIdx = (props.activeIdx - 1 + props.announcements.length) % props.announcements.length;
    const nextIdx = (props.activeIdx + 1) % props.announcements.length;

    const previous = props.announcements[previousIdx];
    const next = props.announcements[nextIdx];

    return {
      current,
      previous,
      next,
    };
  }

  const announcements = getAnnouncements();

  return (
    <div
      className={props.positionStatically ? "_static" : ""}
      css={[
        FONTS.SwarovskiFutura300,
        fontSize.x2ss,
        css`
          height: 72px;
          ${CSS_MQ.atLeastSm} {
            height: 54px;
          }
          ${CSS_MQ.atLeastMd} {
            height: 40px;
          }

          display: flex;
          justify-content: center;
          align-items: center;

          color: ${CSS_COLORS.White};
          background-color: ${CSS_COLORS.GreenDark};

          z-index: 500;
          ${CSS_MQ.atMostSm} {
            z-index: 2147483003; // intercom z-index + 2 (+1 is for header)
          }

          width: 100%;

          &:not(._static) {
            position: absolute;
            top: 0;
          }

          &._static {
            position: relative;
          }

          overflow-x: hidden;

          a {
            color: inherit;
            font-weight: 500;
          }
        `,
      ]}
    >
      {props.announcements.map((ann, idx) => {
        return (
          <Announcement
            key={ann.sys.id}
            ann={ann}
            className={
              ann.sys.id === announcements.previous?.sys.id
                ? "previous"
                : ann.sys.id === announcements.current?.sys.id
                  ? "current"
                  : ann.sys.id === announcements.next?.sys.id
                    ? "next"
                    : "hidden"
            }
          />
        );
      })}

      <button
        onClick={props.onNotificationDismissed}
        css={css`
          --size: 30px;

          position: absolute;
          width: var(--size);
          height: var(--size);

          top: calc(50% - 0.5 * var(--size));
          right: 12px;

          svg {
            width: 100%;
            height: 100%;
          }
        `}
      >
        <CloseButtonSvg />
      </button>
    </div>
  );
};

const containerStyles = css`
  text-align: center;

  padding-left: 0;
  padding-right: 30px;
  ${CSS_MQ.atLeastLg} {
    padding-left: 30px;
  }

  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Announcement: ReactTFC<{ ann: SettingsAnnouncementBarType; className: string }> = props => {
  return (
    <ContentContainer attachStyles={containerStyles}>
      <div
        className={props.className}
        css={css`
          width: 100%;
          background-color: ${CSS_COLORS.GreenDark};

          left: 0;
          top: 0;

          --rot: 55deg;
          --m: 15px;
          --z: 60px;

          &.previous {
            transform: translate3d(0px, calc(-1 * var(--m)), var(--z)) rotateX(var(--rot));
            filter: opacity(0);
          }

          &.current {
            transform: translate3d(0px, 0px, 0px) rotateX(0deg);
            filter: opacity(1);
            z-index: 1;
          }

          &.next {
            transform: translate3d(0px, var(--m), var(--z)) rotateX(calc(var(--rot) * -1));
            filter: opacity(0);
          }

          &.hidden {
            filter: opacity(0);
            transform: translate3d(0px, 0px, 0px) rotateX(0deg);
            z-index: -1;
          }

          transition: 0.3s ease-out;
        `}
      >
        <AnnouncementContent announcement={props.ann} />
      </div>
    </ContentContainer>
  );
};
